/**
 * @module components/Dashboard/MetricsCards
 * @description Tarjetas de KPIs del dashboard principal de cumplimiento ISO.
 *
 * Muestra las métricas agregadas del sistema en tarjetas visuales con progreso,
 * tendencia y color según el nivel de cumplimiento. Los datos provienen de
 * `useMetrics()` (hook del DataContext) que recalcula en tiempo real.
 *
 * ## KPIs mostrados
 * - Cumplimiento global (%)
 * - Controles completados / total
 * - Controles en progreso
 * - Controles vencidos
 * - Tareas pendientes / total
 * - Evidencias subidas
 *
 * Características principales:
 * - Métricas calculadas en tiempo real
 * - Visualización de progreso con barras
 * - Iconos representativos para cada métrica
 * - Diseño responsive y accesible
 * - Colores semánticos para estados
 * - Información contextual adicional
 * 
 * @author Sistema ISO 27001/27002
 * @version 2.0.0 - PostgreSQL Edition
 */

import React from 'react';
import {
  Shield, CheckCircle, Clock, AlertTriangle, TrendingUp,
  ClipboardList, Activity, Flame, Award, Zap
} from 'lucide-react';
import { useData, useMetrics } from '../../contexts/DataContext';
import { AccreditationBadge, useAccreditationLevel } from './AccreditationBadge';

// =====================================================
// INTERFACES Y TIPOS
// =====================================================

/**
 * Interfaz para definir una métrica individual
 */
interface MetricCardData {
  /** Título de la métrica */
  title: string;
  /** Valor numérico principal */
  value: number;
  /** Icono representativo */
  icon: React.ComponentType<{ className?: string }>;
  /** Color temático de la tarjeta */
  color: 'blue' | 'cyan' | 'green' | 'emerald' | 'yellow' | 'red' | 'purple' | 'indigo';
  /** Descripción adicional */
  description: string;
  /** Información de tendencia o contexto */
  trend?: string;
  /** Porcentaje para mostrar barra de progreso */
  percentage?: number;
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function MetricsCards() {
  // =====================================================
  // HOOKS Y DATOS
  // =====================================================
  
  const { filteredControls } = useData();
  const metrics = useMetrics();

  // =====================================================
  // CÁLCULOS DE MÉTRICAS ADICIONALES
  // =====================================================
  
  // Calcular métricas de tareas detalladas
  const allTasks = React.useMemo(() => {
    const tasks = filteredControls.flatMap(control => control.tasks || []);
    if (process.env.NODE_ENV !== 'production') {
      let completed = 0, inProgress = 0, pending = 0;
      for (const t of tasks) {
        if (t.status === 'completed') completed++;
        else if (t.status === 'in_progress') inProgress++;
        else pending++;
      }
      console.log('📊 [METRICS] Calculando métricas de tareas:', {
        totalTasks: tasks.length,
        completedTasks: completed,
        inProgressTasks: inProgress,
        pendingTasks: pending,
        timestamp: new Date().toISOString()
      });
    }
    return tasks;
  }, [filteredControls]);

  // Calcular tareas vencidas
  const overdueTasks = React.useMemo(() => {
    const overdue = allTasks.filter(task => 
      task.status !== 'completed' && new Date(task.dueDate) < new Date()
    );
    console.log('⏰ [METRICS] Tareas vencidas calculadas:', {
      count: overdue.length,
      tasks: overdue.map(t => ({ id: t.id, title: t.title, dueDate: t.dueDate }))
    });
    return overdue;
  }, [allTasks]);

  // Calcular controles críticos
  const criticalControls = React.useMemo(() => {
    const critical = filteredControls.filter(c => c.priority === 'critical');
    console.log('🚨 [METRICS] Controles críticos calculados:', {
      count: critical.length,
      controls: critical.map(c => ({ code: c.code, title: c.title, status: c.status }))
    });
    return critical;
  }, [filteredControls]);

  // Calcular tareas críticas y sus conteos por estado en un solo paso
  const { criticalTasks, criticalTasksCompleted } = React.useMemo(() => {
    const critical = allTasks.filter(t => t.priority === 'critical');
    let completed = 0, inProgress = 0, pending = 0;
    for (const t of critical) {
      if (t.status === 'completed') completed++;
      else if (t.status === 'in_progress') inProgress++;
      else pending++;
    }
    console.log('🔥 [METRICS] Tareas críticas calculadas:', {
      count: critical.length, completed, inProgress, pending,
      tasks: critical.map(t => ({ id: t.id, title: t.title, status: t.status, dueDate: t.dueDate }))
    });
    return { criticalTasks: critical, criticalTasksCompleted: completed };
  }, [allTasks]);

  // =====================================================
  // CONFIGURACIÓN DE MÉTRICAS
  // =====================================================
  
  const criticalControlsCompleted = React.useMemo(
    () => criticalControls.filter(c => c.status === 'completed').length,
    [criticalControls]
  );

  /**
   * Configuración de todas las métricas a mostrar
   */
  const metricsConfig: MetricCardData[] = React.useMemo(() => [
    {
      title: 'Total Controles',
      value: metrics.controls.total,
      icon: Shield,
      color: 'blue',
      description: 'Controles ISO configurados',
      trend: `${metrics.controls.completed} completados`,
      percentage: metrics.controls.completionRate
    },
    {
      title: 'Controles Completados',
      value: metrics.controls.completed,
      icon: CheckCircle,
      color: 'cyan',
      description: 'Controles finalizados',
      trend: `${metrics.controls.inProgress} en progreso`,
      percentage: metrics.controls.completionRate
    },
    {
      title: 'Total Tareas',
      value: metrics.tasks.total,
      icon: ClipboardList,
      color: 'emerald',
      description: 'Tareas creadas en total',
      trend: `${metrics.tasks.pending} pendientes`,
      percentage: metrics.tasks.completionRate
    },
    {
      title: 'Tareas Completadas',
      value: metrics.tasks.completed,
      icon: CheckCircle,
      color: 'green',
      description: 'Tareas finalizadas',
      trend: `${metrics.tasks.total - metrics.tasks.completed} restantes`,
      percentage: metrics.tasks.completionRate
    },
    {
      title: 'Controles Críticos',
      value: criticalControls.length,
      icon: AlertTriangle,
      color: 'red',
      description: 'Controles de alta prioridad',
      trend: overdueTasks.length > 0 ? `${overdueTasks.length} tareas vencidas` : 'Al día',
      percentage: criticalControls.length > 0
        ? (criticalControlsCompleted / criticalControls.length) * 100
        : 0
    },
    {
      title: 'Tareas Críticas',
      value: criticalTasks.length,
      icon: Flame,
      color: 'red',
      description: 'Tareas de prioridad crítica',
      trend: criticalTasksCompleted > 0
        ? `${criticalTasksCompleted} completadas`
        : 'Requieren atención inmediata',
      percentage: criticalTasks.length > 0
        ? (criticalTasksCompleted / criticalTasks.length) * 100
        : 0
    }
  ], [metrics, criticalControls, criticalControlsCompleted, criticalTasks, criticalTasksCompleted, overdueTasks]);

  // =====================================================
  // FUNCIONES DE UTILIDAD
  // =====================================================
  
  /**
   * Obtiene las clases CSS para el color de una métrica
   * @param color - Color de la métrica
   * @returns Clases CSS para el color
   */
  const getColorClasses = (color: MetricCardData['color']): string => {
    switch (color) {
      case 'blue': return 'bg-blue-500 text-blue-50';
      case 'cyan': return 'bg-cyan-500 text-cyan-50';
      case 'green': return 'bg-green-500 text-green-50';
      case 'emerald': return 'bg-emerald-500 text-emerald-50';
      case 'yellow': return 'bg-yellow-500 text-yellow-50';
      case 'red': return 'bg-red-500 text-red-50';
      case 'purple': return 'bg-purple-500 text-purple-50';
      case 'indigo': return 'bg-indigo-500 text-indigo-50';
      default: return 'bg-gray-500 text-gray-50';
    }
  };

  /**
   * Obtiene el color de la barra de progreso según el porcentaje
   * @param percentage - Porcentaje de progreso
   * @param color - Color base de la métrica
   * @returns Clase CSS para la barra de progreso
   */
  const getProgressBarColor = (percentage: number, color: MetricCardData['color']): string => {
    // Para métricas críticas, usar colores semánticos
    if (color === 'red') {
      return percentage >= 80 ? 'bg-green-500' : percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500';
    }

    // Para otras métricas, usar el color base
    switch (color) {
      case 'blue': return 'bg-blue-500';
      case 'cyan': return 'bg-cyan-500';
      case 'green': return 'bg-green-500';
      case 'emerald': return 'bg-emerald-500';
      case 'yellow': return 'bg-yellow-500';
      case 'purple': return 'bg-purple-500';
      case 'indigo': return 'bg-indigo-500';
      default: return 'bg-gray-500';
    }
  };

  // =====================================================
  // LOGGING PARA DEBUGGING
  // =====================================================
  
  React.useEffect(() => {
    console.log('📊 [METRICS CARDS] Métricas actualizadas:', {
      controls: {
        total: metrics.controls.total,
        completed: metrics.controls.completed,
        completionRate: metrics.controls.completionRate.toFixed(1) + '%'
      },
      tasks: {
        total: metrics.tasks.total,
        completed: metrics.tasks.completed,
        completionRate: metrics.tasks.completionRate.toFixed(1) + '%'
      },
      criticalTasks: {
        total: criticalTasks.length,
        completed: criticalTasks.filter(t => t.status === 'completed').length,
        inProgress: criticalTasks.filter(t => t.status === 'in_progress').length,
        pending: criticalTasks.filter(t => t.status === 'pending').length
      },
      timestamp: new Date().toISOString()
    });
  }, [metrics, criticalTasks]);

  // =====================================================
  // RENDERIZADO DEL COMPONENTE
  // =====================================================

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
      {/* ===== TARJETAS DE MÉTRICAS INDIVIDUALES ===== */}
      {metricsConfig.map((metric) => {
        const IconComponent = metric.icon;
        const progressPercentage = metric.percentage || 0;
        
        return (
          <div
            key={metric.title}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-all duration-200 hover:scale-105"
          >
            {/* Icono de la métrica - Arriba */}
            <div className="flex justify-between items-start mb-4">
              <div className={`p-3 rounded-lg ${getColorClasses(metric.color)}`}>
                <IconComponent className="w-6 h-6" />
              </div>
            </div>

            {/* Contenido de la métrica */}
            <div className="space-y-1">
              {/* Título de la métrica */}
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {metric.title}
              </p>

              {/* Valor principal */}
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {metric.value.toLocaleString()}
              </p>

              {/* Descripción */}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {metric.description}
              </p>

              {/* Información de tendencia */}
              {metric.trend && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 font-medium">
                  {metric.trend}
                </p>
              )}
            </div>

            {/* Barra de progreso */}
            {metric.percentage !== undefined && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                  <span>Progreso</span>
                  <span className="font-medium">{progressPercentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${getProgressBarColor(progressPercentage, metric.color)}`}
                    style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                    role="progressbar"
                    aria-valuenow={progressPercentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${metric.title}: ${progressPercentage.toFixed(1)}%`}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* ===== TARJETA DE RESUMEN DE CUMPLIMIENTO ===== */}
      <div className="md:col-span-2 lg:col-span-3 xl:col-span-4 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 rounded-xl shadow-lg p-6 text-white relative overflow-hidden">
        {/* Efecto de fondo animado */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-transparent animate-pulse"></div>

        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <Award className="w-6 h-6 text-yellow-300" />
                <h3 className="text-lg font-semibold">Tasa de Cumplimiento General</h3>
                {/* Badge de estado */}
                {metrics.controls.completionRate >= 80 && (
                  <span className="px-2 py-0.5 bg-green-400/90 text-green-900 text-xs font-bold rounded-full animate-pulse">
                    ¡Excelente!
                  </span>
                )}
                {metrics.controls.completionRate >= 50 && metrics.controls.completionRate < 80 && (
                  <span className="px-2 py-0.5 bg-yellow-400/90 text-yellow-900 text-xs font-bold rounded-full">
                    Bueno
                  </span>
                )}
                {metrics.controls.completionRate < 50 && (
                  <span className="px-2 py-0.5 bg-orange-400/90 text-orange-900 text-xs font-bold rounded-full">
                    En Progreso
                  </span>
                )}
              </div>
              <p className="text-blue-100 mb-3 text-sm">
                Progreso integral del programa de cumplimiento ISO 27001/27002
              </p>

              {/* Cuadro de Controles */}
              <div className="text-sm">
                <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm border border-white/20 max-w-xs">
                  <div className="flex items-center space-x-2 mb-1">
                    <Shield className="w-4 h-4 text-blue-200" />
                    <p className="text-blue-200 font-medium">Controles</p>
                  </div>
                  <p className="font-bold text-lg">
                    {metrics.controls.completed} / {metrics.controls.total}
                  </p>
                  <p className="text-xs text-blue-200 mt-0.5">completados</p>
                </div>
              </div>
            </div>

            {/* Badge de Acreditación ACSGC */}
            <div className="flex flex-col items-center ml-4">
              <AccreditationBadge
                completionRate={metrics.controls.completionRate}
                size="md"
                showLabel={true}
                showMessage={false}
              />
              <div className={`text-2xl font-bold mt-2 ${metrics.controls.completionRate >= 80 ? 'text-yellow-300' : 'text-white'}`}>
                {metrics.controls.completionRate.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Barra de progreso mejorada con gradiente animado */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-blue-100 font-medium">Progreso General</span>
              <span className="text-white font-bold">{metrics.controls.completionRate.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-blue-900/40 rounded-full h-4 shadow-inner border border-white/10">
              <div
                className={`h-4 rounded-full transition-all duration-700 ease-out ${
                  metrics.controls.completionRate >= 80
                    ? 'bg-gradient-to-r from-green-400 to-emerald-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]'
                    : metrics.controls.completionRate >= 50
                    ? 'bg-gradient-to-r from-yellow-400 to-orange-400'
                    : 'bg-gradient-to-r from-blue-400 to-blue-500'
                }`}
                style={{ width: `${Math.min(metrics.controls.completionRate, 100)}%` }}
                role="progressbar"
                aria-valuenow={metrics.controls.completionRate}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Cumplimiento general: ${metrics.controls.completionRate.toFixed(1)}%`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ===== TARJETA DE ESTADO DE TAREAS ===== */}
      <div className="md:col-span-2 lg:col-span-3 xl:col-span-2 bg-gradient-to-br from-emerald-500 via-green-600 to-green-700 rounded-xl shadow-lg p-6 text-white relative overflow-hidden">
        {/* Efecto de fondo */}
        <div className="absolute inset-0 bg-gradient-to-r from-green-400/10 to-transparent"></div>

        <div className="relative z-10">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="flex items-center space-x-2">
                <Activity className="w-6 h-6 text-green-200" />
                <h3 className="text-lg font-semibold">Estado de Tareas</h3>
              </div>
              <p className="text-green-100 text-sm mt-1">Distribución y progreso actual</p>
            </div>

            {/* Mini gráfico circular con porcentaje */}
            <div className="relative">
              <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                {/* Círculo de fondo */}
                <circle
                  cx="18"
                  cy="18"
                  r="15.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  className="text-green-900/30"
                />
                {/* Círculo de progreso */}
                <circle
                  cx="18"
                  cy="18"
                  r="15.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray={`${metrics.tasks.completionRate * 0.974} 100`}
                  className="text-yellow-300 transition-all duration-700"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-bold">{metrics.tasks.completionRate.toFixed(0)}%</span>
              </div>
            </div>
          </div>

          <div className="space-y-2.5">
            {/* Tareas completadas */}
            <div className="bg-white/10 rounded-lg p-2.5 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-all cursor-default group">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-green-400 rounded-md group-hover:scale-110 transition-transform">
                    <CheckCircle className="w-4 h-4 text-green-900" />
                  </div>
                  <span className="text-green-50 font-medium text-sm">Completadas</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="font-bold text-lg">{metrics.tasks.completed}</span>
                  <span className="text-xs text-green-200">/{metrics.tasks.total}</span>
                </div>
              </div>
            </div>

            {/* Tareas en progreso */}
            <div className="bg-white/10 rounded-lg p-2.5 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-all cursor-default group">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-blue-400 rounded-md group-hover:scale-110 transition-transform">
                    <Zap className="w-4 h-4 text-blue-900" />
                  </div>
                  <span className="text-green-50 font-medium text-sm">En Progreso</span>
                </div>
                <span className="font-bold text-lg">{metrics.tasks.inProgress}</span>
              </div>
            </div>

            {/* Tareas pendientes */}
            <div className="bg-white/10 rounded-lg p-2.5 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-all cursor-default group">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-yellow-400 rounded-md group-hover:scale-110 transition-transform">
                    <Clock className="w-4 h-4 text-yellow-900" />
                  </div>
                  <span className="text-green-50 font-medium text-sm">Pendientes</span>
                </div>
                <span className="font-bold text-lg">{metrics.tasks.pending}</span>
              </div>
            </div>

            {/* Tareas vencidas - con badge pulsante si hay alguna */}
            <div className={`bg-white/10 rounded-lg p-2.5 backdrop-blur-sm border hover:bg-white/15 transition-all cursor-default group ${
              overdueTasks.length > 0 ? 'border-red-400/50 animate-pulse' : 'border-white/20'
            }`}>
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <div className={`p-1.5 rounded-md group-hover:scale-110 transition-transform ${
                    overdueTasks.length > 0 ? 'bg-red-500 animate-pulse' : 'bg-gray-400'
                  }`}>
                    <AlertTriangle className={`w-4 h-4 ${overdueTasks.length > 0 ? 'text-white' : 'text-gray-700'}`} />
                  </div>
                  <span className="text-green-50 font-medium text-sm">Vencidas</span>
                  {overdueTasks.length > 0 && (
                    <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                      ¡{overdueTasks.length}!
                    </span>
                  )}
                </div>
                <span className={`font-bold text-lg ${overdueTasks.length > 0 ? 'text-red-300' : ''}`}>
                  {overdueTasks.length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}